import { UseMutationResult, UseQueryResult, useMutation, useQuery } from '@tanstack/react-query'

import { ApplicationException } from '@/common/exceptions'
import { useErrorHandler } from '@/common/hooks'

import {
  CreateHelpSystemRequirementHookParams,
  ShowIpHookParams,
  ShowIpHookResult,
  ShowServicesStatusHookParams,
  ShowServicesStatusHookResult,
  ShowSpeedTestHookParams,
  ShowSystemRequirementHookParams,
  ShowSystemRequirementHookResult
} from './contracts/hook'
import {
  createHelpSystemRequirementService,
  showIpService,
  showServicesStatusService,
  showSystemRequirementService
} from './services'

export const hookKey = 'requirement'

export function useShowSystemRequirement({
  queryOptions,
  onSuccess,
  onError
}: ShowSystemRequirementHookParams): UseQueryResult<
  ShowSystemRequirementHookResult,
  ApplicationException
> {
  const { handleError } = useErrorHandler()

  return useQuery({
    queryKey: [hookKey, 'show'],
    queryFn: async () => {
      try {
        const listSystemRequirement = await showSystemRequirementService()
        onSuccess?.(listSystemRequirement)
        return listSystemRequirement
      } catch (error) {
        const parsedError = error as ApplicationException
        handleError({ error: parsedError })
        onError?.({ error: parsedError })
        throw parsedError
      }
    },
    ...queryOptions
  })
}

export function useCreateHelpSystemRequirement(): UseMutationResult<
  null,
  ApplicationException,
  CreateHelpSystemRequirementHookParams
> {
  const { handleError } = useErrorHandler()

  return useMutation({
    mutationKey: [hookKey, 'createHelp'],
    mutationFn: async ({ model, onSuccess, onError }: CreateHelpSystemRequirementHookParams) => {
      try {
        const resultSystemRequirement = await createHelpSystemRequirementService(model)
        onSuccess?.(resultSystemRequirement)
        return resultSystemRequirement
      } catch (error) {
        const parsedError = error as ApplicationException
        onError?.({ error: parsedError })
        handleError({ error: parsedError })
        throw parsedError
      }
    }
  })
}

export function useShowServicesStatus({
  queryOptions,
  onSuccess,
  onError
}: ShowServicesStatusHookParams): UseQueryResult<
  ShowServicesStatusHookResult,
  ApplicationException
> {
  const { handleError } = useErrorHandler()

  return useQuery({
    queryKey: [hookKey, 'showServiceStatus'],
    queryFn: async () => {
      try {
        const showServicesStatus = await showServicesStatusService()
        onSuccess?.(showServicesStatus)
        return showServicesStatus
      } catch (error) {
        const parsedError = error as ApplicationException
        handleError({ error: parsedError })
        onError?.({ error: parsedError })
        throw parsedError
      }
    },
    ...queryOptions
  })
}

export function useShowIp({
  queryOptions,
  onSuccess,
  onError
}: ShowIpHookParams): UseQueryResult<ShowIpHookResult, ApplicationException> {
  const { handleError } = useErrorHandler()

  return useQuery({
    queryKey: [hookKey, 'showIp'],
    queryFn: async () => {
      try {
        const showIp = await showIpService()
        onSuccess?.(showIp)
        return showIp
      } catch (error) {
        const parsedError = error as ApplicationException
        handleError({ error: parsedError })
        onError?.({ error: parsedError })
        throw parsedError
      }
    },
    ...queryOptions
  })
}

export function useShowSpeedTest({
  queryOptions,
  onSuccess,
  onError
}: ShowSpeedTestHookParams): UseQueryResult<number, ApplicationException> {
  const { handleError } = useErrorHandler()
  const imageUrl = import.meta.env.VITE_S3_FILE_DOWNLOAD || '/arquivo_download.bin'
  const bitsInByte = 8
  const kilobitsInBit = 1024
  const megabitsInKilobit = 1024
  const millisecondsInSecond = 1000

  const decimalPlaces = 2

  const startTime = new Date().getTime()

  return useQuery({
    queryKey: [hookKey, 'showSpeed'],
    queryFn: async () => {
      try {
        const response = await fetch(imageUrl, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0'
          }
        })
        const blob = await response.blob()
        const endTime = new Date().getTime()
        const durationInSeconds = (endTime - startTime) / millisecondsInSecond
        const imageSizeInBits = blob.size * bitsInByte
        const speedBps = imageSizeInBits / durationInSeconds
        const speedKbps = speedBps / kilobitsInBit
        const speedMbps = (speedKbps / megabitsInKilobit) * bitsInByte

        onSuccess?.(Number(speedMbps))

        return Number(speedMbps.toFixed(decimalPlaces))
      } catch (error) {
        const parsedError = error as ApplicationException
        handleError({ error: parsedError })
        onError?.({ error: parsedError })
        throw parsedError
      }
    },
    ...queryOptions
  })
}
